import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Card, Button, Toast, List, Tag } from 'antd-mobile';
import { LeftOutline, ClockCircleOutline, MessageOutline, RightOutline } from 'antd-mobile-icons';
import { useNavigate, useParams } from 'react-router-dom';
import { getMyScenarioSessions, type SessionInfo } from '../../lib/api/conversations';

const Container = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding-bottom: 20px;
`;

const Header = styled.div`
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  position: relative;
`;

const BackButton = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 10;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
`;

const HeaderContent = styled.div`
  padding-top: 40px;
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: bold;
  margin: 0;
  color: white;
`;

const ContentSection = styled.div`
  padding: 16px;
`;

const SessionCard = styled(Card)`
  margin-bottom: 12px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }
`;

const SessionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const SessionTitle = styled.div`
  font-weight: 600;
  color: #333;
  font-size: 16px;
`;

const SessionStatusTag = styled(Tag)<{ statustype: string }>`
  background: ${props => {
    switch (props.statustype) {
      case '1': return '#52c41a'; // 进行中
      case '2': return '#1890ff'; // 已完成
      case '3': return '#faad14'; // 已暂停
      case '4': return '#ff4d4f'; // 已取消
      default: return '#d9d9d9';
    }
  }};
  color: white;
  border: none;
  border-radius: 12px;
`;

const SessionInfo = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: #666;
  font-size: 14px;
`;

const SessionMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #999;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

interface SessionItem {
  id: string;
  session_token: string;
  scenario_name: string;
  status: number;
  status_name: string;
  round_number: number;
  created_at: string;
  updated_at: string;
}

const ScenarioSessions: React.FC = () => {
  const navigate = useNavigate();
  const { scenarioId } = useParams<{ scenarioId: string }>();
  const [loading, setLoading] = useState(false);
  const [sessions, setSessions] = useState<SessionItem[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const fetchSessions = async (page: number = 1) => {
    if (!scenarioId) return;
    
    setLoading(true);
    try {
      const response = await getMyScenarioSessions({
        scenario_id: scenarioId,
        page,
        pageSize
      });
      
      if (response.success && response.data) {
        const sessionData = response.data.list.map((session: SessionInfo) => ({
          id: session.id,
          session_token: session.session_token,
          scenario_name: session.scenario_name,
          status: session.status,
          status_name: session.status_name,
          round_number: session.round_number,
          created_at: session.created_at,
          updated_at: session.updated_at
        }));
        
        setSessions(sessionData);
        setTotal(response.data.total);
        setCurrentPage(response.data.current);
      } else {
        throw new Error(response.errorMessage || '获取数据失败');
      }
    } catch (error) {
      console.error('获取会话列表失败:', error);
      Toast.show({
        content: '获取对话记录失败',
        icon: 'fail'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSessions();
  }, [scenarioId]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleSessionClick = (session: SessionItem) => {
    navigate(`/learn/session/${session.id}`);
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return '进行中';
      case 'completed': return '已完成';
      case 'interrupted': return '已中断';
      case 'error': return '错误';
      default: return status;
    }
  };

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleString('zh-CN', {
      month: 'numeric',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '-';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <Container>
        <Header>
          <BackButton onClick={handleBack}>
            <LeftOutline fontSize={18} />
          </BackButton>
          <HeaderContent>
            <HeaderTitle>对话记录</HeaderTitle>
          </HeaderContent>
        </Header>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          加载中...
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={handleBack}>
          <LeftOutline fontSize={18} />
        </BackButton>
        <HeaderContent>
          <HeaderTitle>对话记录</HeaderTitle>
        </HeaderContent>
      </Header>

      <ContentSection>
        {sessions.length === 0 ? (
          <EmptyState>
            <EmptyIcon>💬</EmptyIcon>
            <div>暂无对话记录</div>
            <div style={{ fontSize: '12px', marginTop: '8px' }}>开始第一次陪练吧！</div>
          </EmptyState>
        ) : (
          sessions.map((session) => (
            <SessionCard key={session.id} onClick={() => handleSessionClick(session)}>
              <SessionHeader>
                <SessionTitle>对话 #{session.id}</SessionTitle>
                <SessionStatusTag statustype={session.status.toString()}>
                  {session.status_name}
                </SessionStatusTag>
              </SessionHeader>
              <SessionInfo>
                <SessionMeta>
                  <MetaItem>
                    <MessageOutline fontSize={14} />
                    <span>{session.round_number} 轮</span>
                  </MetaItem>
                  <MetaItem>
                    <ClockCircleOutline fontSize={14} />
                    <span>{session.status === 2 ? '已完成' : '进行中'}</span>
                  </MetaItem>
                </SessionMeta>
                <div>
                  {formatTime(session.created_at)}
                  <RightOutline fontSize={14} style={{ marginLeft: '8px', opacity: 0.5 }} />
                </div>
              </SessionInfo>
            </SessionCard>
          ))
        )}
      </ContentSection>
    </Container>
  );
};

export default ScenarioSessions;